import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import HeaderSection from '../../components/home/HeaderSection';
import FooterSection from '../../components/home/FooterSection';
import CourseDetailHero from '../../components/courseDetail/CourseDetailHero';
import CourseInfoBar from '../../components/courseDetail/CourseInfoBar';
import CourseDetailTabs from '../../components/courseDetail/CourseDetailTabs';
import CourseDetailsSection from '../../components/courseDetail/CourseDetailsSection';
import WhatYouLearnSection from '../../components/courseDetail/WhatYouLearnSection';
import CourseContentSection from '../../components/courseDetail/CourseContentSection';
import InstructorProfileSection from '../../components/courseDetail/InstructorProfileSection';
import StudentsReviewSection from '../../components/courseDetail/StudentsReviewSection';
import CourseSubscriptionSidebar from '../../components/courseDetail/CourseSubscriptionSidebar';
import { useAuthContext } from '../../context/AuthContext';
import { useSubscriptionContext } from '../../context/SubscriptionContext';
import { ROUTES } from '../../constants/routes';
import { useCanonicalCourseUrl } from '../../hooks/useCanonicalCourseUrl';
import { useCourseDetail } from '../../hooks/useCourseDetail';
import { parseCourseIdFromSlug } from '../../utils/courseSlug';

function CourseDetailPage() {
  const { courseId: courseSlug } = useParams();
  const courseId = parseCourseIdFromSlug(courseSlug);
  const navigate = useNavigate();
  const { isLoggedIn, openAuthPopup } = useAuthContext();
  const { isCategorySubscribed } = useSubscriptionContext();
  const { course, chapters, reviews, loading, error, refetchReviews } = useCourseDetail(courseId);
  const [activeTab, setActiveTab] = useState('Overview');
  useCanonicalCourseUrl(course);

  const isSubscribed = useMemo(() => {
    if (!course) return false;
    if (course.isSubscriptionTaken) return true;
    if (course.categoryId == null) return false;
    return isCategorySubscribed(course.categoryId);
  }, [course, isCategorySubscribed]);

  const isFreeCourse = !course?.isPaid;
  const hasFullAccess = isSubscribed || isFreeCourse;

  const previewLesson = useMemo(() => {
    for (const chapter of chapters) {
      const lesson = chapter.items?.find(
        (item) => item.type === 'lecture' && item.isFreePreview
      );
      if (lesson) return lesson;
    }
    return null;
  }, [chapters]);

  const handleStartLearning = () => {
    navigate(ROUTES.COURSE_PLAYLIST(course || courseId));
  };

  const handleRequireSubscription = () => {
    navigate(ROUTES.PRICING);
  };

  if (loading) {
    return (
      <>
        <HeaderSection />
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="h-[420px] animate-pulse rounded-2xl bg-white/80" />
        </div>
        <FooterSection />
      </>
    );
  }

  if (error || !course) {
    return (
      <>
        <HeaderSection />
        <div className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6 lg:px-8">
          <p className="text-red-600">{error || 'Course not found.'}</p>
        </div>
        <FooterSection />
      </>
    );
  }

  const tabContent = (
    <div className="space-y-8 pt-6">
      {activeTab === 'Overview' && (
        <>
          <CourseDetailsSection content={course.courseDetails} />
          {course.whatYouLearn.length > 0 && <WhatYouLearnSection items={course.whatYouLearn} />}
          <CourseContentSection
            chapters={chapters}
            hasFullAccess={hasFullAccess}
            course={course}
            courseId={courseId}
          />
          <InstructorProfileSection instructor={course.instructor} />
          <StudentsReviewSection reviews={reviews} />
        </>
      )}
      {activeTab === 'Courses' && (
        <CourseContentSection
          chapters={chapters}
          hasFullAccess={hasFullAccess}
          course={course}
          courseId={courseId}
        />
      )}
      {activeTab === 'Review' && (
        <StudentsReviewSection
          reviews={reviews}
          courseId={courseId}
          canWriteReview={isLoggedIn}
          onReviewCreated={refetchReviews}
        />
      )}
    </div>
  );

  return (
    <>
      <HeaderSection />

      <CourseDetailHero course={course} />

      <div className="">
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <CourseInfoBar instructor={course.instructor} stats={course.stats} />

          <div className="pb-16 pt-4 lg:pb-20 lg:pt-6">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-6">
              <div className="min-w-0">
                <CourseDetailTabs activeTab={activeTab} onTabChange={setActiveTab} />
                {tabContent}
              </div>

              <aside className="mt-8 lg:mt-0 lg:min-h-0">
                <div className="lg:sticky lg:top-[96px] lg:z-20 xl:top-[120px] lg:-mt-[250px]">
                  <CourseSubscriptionSidebar
                    subscription={course.subscription}
                    previewLesson={previewLesson}
                    hasFullAccess={hasFullAccess}
                    isFreeCourse={isFreeCourse}
                    isLoggedIn={isLoggedIn}
                    onStartLearning={handleStartLearning}
                    onRequireAuth={openAuthPopup}
                    onRequireSubscription={handleRequireSubscription}
                  />
                </div>
              </aside>
            </div>
          </div>
        </div>
      </div>

      <FooterSection />
    </>
  );
}

export default CourseDetailPage;
